import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Button, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip, CircularProgress, Snackbar, Alert, Dialog
} from '@mui/material';
import { Add, Delete, Edit, Print } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

// Types for props
interface SpecimenPart {
  id: number;
  code: string;
  description: string;
  site?: string;
}
interface Block {
  id: number;
  order_specimen_part_id: number;
  block_id: string;
  cassette_label: string;
  slides: number;
  stains: string;
  status: string;
  slide_labels: string;
  technician: string;
}

interface HistologySectionProps {
  order: any;
  parts: SpecimenPart[];
  disabled?: boolean;
}

const STAIN_OPTIONS = [
  'H&E', 'Pap Stain', 'Diff-Quik', 'Unstained', 'PAS', 'GMS', 'Ziehl-Neelsen (AFB)', 'Trichrome', 'Reticulin',
  'Prussian Blue (Iron)', 'Congo Red', 'Mucicarmine', 'Alcian Blue', 'Oil Red O', 'Toluidine Blue',
  'Warthin-Starry', 'Fontana-Masson', 'CK7', 'CK20', 'CDX2', 'p53', 'ER', 'PR', 'HER2', 'Ki-67', 'TTF-1',
  'Napsin A', 'CD3', 'CD20', 'CD30', 'CD138', 'BCL2', 'BCL6', 'SOX10', 'S100', 'Melan-A', 'Synaptophysin',
  'Chromogranin', 'EBER (ISH)', 'ThinPrep', 'SurePath', 'Control Slide', 'Level', 'Recut', 'Frozen Section',
  'Rapid H&E', 'QC Slide', 'Negative Control', 'Positive Control'
];

const HistologySection: React.FC<HistologySectionProps> = ({ order, parts, disabled }) => {
  const [selectedPartIdx, setSelectedPartIdx] = useState(0);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [blockToEdit, setBlockToEdit] = useState<Block | null>(null);
  const [newBlockStain, setNewBlockStain] = useState<string>('');
  const [editBlockStain, setEditBlockStain] = useState<string>('');
  const [newBlockId, setNewBlockId] = useState('');
  const [newCassetteLabel, setNewCassetteLabel] = useState('');
  const [newSlides, setNewSlides] = useState<number>(1);
  const [newSlideLabels, setNewSlideLabels] = useState('');

  const [editBlockId, setEditBlockId] = useState('');
  const [editCassetteLabel, setEditCassetteLabel] = useState('');
  const [editSlides, setEditSlides] = useState<number>(1);
  const [editSlideLabels, setEditSlideLabels] = useState('');

  // Fetch blocks for the selected part
  useEffect(() => {
    if (!parts[selectedPartIdx]) return;
    const fetchBlocks = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(
          `/api/histology?order_id=${order.id}&specimen_part_id=${parts[selectedPartIdx].id}&part_code=${encodeURIComponent(parts[selectedPartIdx].code)}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch histology blocks');
        const data = await res.json();
        setBlocks(data);
      } catch (e: any) {
        setError(e.message || 'Error fetching histology blocks');
        setBlocks([]);
      }
      setLoading(false);
    };
    fetchBlocks();
  }, [selectedPartIdx, parts, order.id]);

  // Helper to get next block number for a part
  const getNextBlockNumber = useCallback(() => {
    const partCode = parts[selectedPartIdx]?.code || '';
    const partBlocks = blocks.filter(b => b.block_id && b.block_id.startsWith(partCode));
    let maxNum = 0;
    partBlocks.forEach(b => {
      const match = b.block_id.match(new RegExp(`^${partCode}(\\d+)$`));
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > maxNum) maxNum = num;
      }
    });
    return maxNum + 1;
  }, [selectedPartIdx, parts, blocks]);

  // Auto-populate Add Block dialog fields when opened or part changes
  useEffect(() => {
    if (!addDialogOpen || !parts[selectedPartIdx]) return;
    const partCode = parts[selectedPartIdx].code;
    const nextNum = getNextBlockNumber();
    const blockId = `${partCode}${nextNum}`;
    setNewBlockId(blockId);
    setNewCassetteLabel(blockId);
    setNewSlides(1);
    setNewSlideLabels(`${blockId}-1`);
  }, [addDialogOpen, selectedPartIdx, parts, getNextBlockNumber]);

  // Auto-generate slide labels when slides number changes (Add Block)
  useEffect(() => {
    if (!addDialogOpen) return;
    const blockId = newBlockId;
    if (!blockId || !newSlides || newSlides < 1) return;
    const labels = Array.from({ length: newSlides }, (_, i) => `${blockId}-${i + 1}`).join(', ');
    setNewSlideLabels(labels);
  }, [newSlides, newBlockId, addDialogOpen]);

  // Auto-populate Edit Block dialog fields when opened
  useEffect(() => {
    if (!editDialogOpen || !blockToEdit) return;
    setEditBlockId(blockToEdit.block_id || '');
    setEditCassetteLabel(blockToEdit.cassette_label || '');
    setEditSlides(blockToEdit.slides || 1);
    setEditSlideLabels(blockToEdit.slide_labels || (blockToEdit.block_id ? `${blockToEdit.block_id}-1` : ''));
  }, [editDialogOpen, blockToEdit]);

  // Auto-generate slide labels when slides number changes (Edit Block)
  useEffect(() => {
    if (!editDialogOpen) return;
    const blockId = editBlockId;
    if (!blockId || !editSlides || editSlides < 1) return;
    const labels = Array.from({ length: editSlides }, (_, i) => `${blockId}-${i + 1}`).join(', ');
    setEditSlideLabels(labels);
  }, [editSlides, editBlockId, editDialogOpen]);

  // Add block
  const handleAddBlock = async () => {
    if (!parts[selectedPartIdx]) return;
    try {
      const token = localStorage.getItem('token');
      const newBlock = {
        order_specimen_part_id: parts[selectedPartIdx].id,
        block_id: `B${blocks.length + 1}`,
        cassette_label: `B${blocks.length + 1}`,
        slides: 0,
        stains: '',
        status: 'In Progress',
        slide_labels: '',
        technician: '',
      };
      const res = await fetch('/api/histology', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(newBlock)
      });
      if (!res.ok) throw new Error('Failed to add block');
      setSnackbar('Block added');
      // Refresh blocks
      const data = await res.json();
      setBlocks(prev => [...prev, data]);
    } catch (e: any) {
      setError(e.message || 'Error adding block');
    }
  };

  // Remove block
  const handleRemoveBlock = async (blockId: number) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/histology/${blockId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to delete block');
      setSnackbar('Block deleted');
      setBlocks(prev => prev.filter(b => b.id !== blockId));
    } catch (e: any) {
      setError(e.message || 'Error deleting block');
    }
  };

  // Add block dialog logic
  const handleOpenAddDialog = () => {
    setNewBlockStain('');
    setAddDialogOpen(true);
  };
  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
  };
  const handleAddBlockConfirm = async () => {
    if (!parts[selectedPartIdx]) return;
    try {
      const token = localStorage.getItem('token');
      const newBlock = {
        order_specimen_part_id: parts[selectedPartIdx].id,
        block_id: newBlockId,
        cassette_label: newCassetteLabel,
        slides: newSlides,
        stains: newBlockStain,
        status: 'In Progress',
        slide_labels: newSlideLabels,
        technician: '',
      };
      const res = await fetch('/api/histology', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(newBlock)
      });
      if (!res.ok) throw new Error('Failed to add block');
      setSnackbar('Block added');
      const data = await res.json();
      setBlocks(prev => [...prev, data]);
      setAddDialogOpen(false);
    } catch (e: any) {
      setError(e.message || 'Error adding block');
    }
  };

  // Edit block dialog logic
  const handleEditBlock = (block: Block) => {
    setBlockToEdit(block);
    setEditBlockStain(block.stains || '');
    setEditDialogOpen(true);
  };
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setBlockToEdit(null);
  };
  const handleEditBlockConfirm = async () => {
    if (!blockToEdit) return;
    try {
      const token = localStorage.getItem('token');
      const updatedBlock = {
        ...blockToEdit,
        block_id: editBlockId,
        cassette_label: editCassetteLabel,
        slides: editSlides,
        stains: editBlockStain,
        slide_labels: editSlideLabels,
      };
      const res = await fetch(`/api/histology/${blockToEdit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(updatedBlock)
      });
      if (!res.ok) throw new Error('Failed to update block');
      setSnackbar('Block updated');
      setBlocks(prev => prev.map(b => b.id === blockToEdit.id ? { ...b, ...updatedBlock } : b));
      setEditDialogOpen(false);
      setBlockToEdit(null);
    } catch (e: any) {
      setError(e.message || 'Error updating block');
    }
  };

  const selectedPart = parts[selectedPartIdx];
  const partBlocks = blocks;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Histology Details</Typography>
      {/* Tabs for parts */}
      <Tabs
        value={selectedPartIdx}
        onChange={(_, idx) => setSelectedPartIdx(idx)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {parts.map((part, idx) => (
          <Tab key={part.code} label={`Part ${part.code} - ${part.description}`} />
        ))}
      </Tabs>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Block ID</TableCell>
              <TableCell>Cassette Label</TableCell>
              <TableCell>Slides</TableCell>
              <TableCell>Stains</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Slide Labels</TableCell>
              <TableCell>Technician</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {partBlocks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  (No blocks entered yet)
                </TableCell>
              </TableRow>
            ) : partBlocks.map((block) => (
              <TableRow key={block.id}>
                <TableCell>{block.block_id}</TableCell>
                <TableCell>{block.cassette_label}</TableCell>
                <TableCell>{block.slides}</TableCell>
                <TableCell>{block.stains}</TableCell>
                <TableCell>{block.status}</TableCell>
                <TableCell>{block.slide_labels}</TableCell>
                <TableCell>{block.technician}</TableCell>
                <TableCell>
                  <Tooltip title="Edit"><span><IconButton disabled={disabled} onClick={() => handleEditBlock(block)}><Edit /></IconButton></span></Tooltip>
                  <Tooltip title="Delete"><span><IconButton disabled={disabled} onClick={() => handleRemoveBlock(block.id)}><Delete /></IconButton></span></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button startIcon={<Add />} onClick={handleOpenAddDialog} disabled={disabled}>Add Block</Button>
        <Button startIcon={<Add />} disabled={disabled}>Add Slide</Button>
        <Button startIcon={<Print />} disabled={disabled}>Print Labels</Button>
        <Button disabled={disabled}>View Embedding Log</Button>
      </Box>
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(null)} severity="info" sx={{ width: '100%' }}>{snackbar}</Alert>
      </Snackbar>
      {/* Add Block Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog}>
        <Box sx={{ p: 3, minWidth: 300 }}>
          <Typography variant="h6" gutterBottom>Add Block</Typography>
          <TextField label="Block ID" value={newBlockId} onChange={e => setNewBlockId(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Cassette Label" value={newCassetteLabel} onChange={e => setNewCassetteLabel(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <Autocomplete
            options={STAIN_OPTIONS}
            value={newBlockStain}
            onChange={(_, value) => setNewBlockStain(value || '')}
            renderInput={(params) => <TextField {...params} label="Stain" />}
            fullWidth
            disableClearable
            sx={{ mb: 2 }}
          />
          <TextField
            label="Slides"
            type="number"
            value={newSlides}
            onChange={e => setNewSlides(Math.max(1, parseInt(e.target.value) || 1))}
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Slide Labels"
            value={newSlideLabels}
            onChange={e => setNewSlideLabels(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            helperText="Comma-separated labels. Auto-generated when slides changes."
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleCloseAddDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleAddBlockConfirm} disabled={!newBlockId || !newCassetteLabel || !newBlockStain || !newSlides || !newSlideLabels}>Add</Button>
          </Box>
        </Box>
      </Dialog>
      {/* Edit Block Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog}>
        <Box sx={{ p: 3, minWidth: 300 }}>
          <Typography variant="h6" gutterBottom>Edit Block</Typography>
          <TextField label="Block ID" value={editBlockId} onChange={e => setEditBlockId(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Cassette Label" value={editCassetteLabel} onChange={e => setEditCassetteLabel(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <Autocomplete
            options={STAIN_OPTIONS}
            value={editBlockStain}
            onChange={(_, value) => setEditBlockStain(value || '')}
            renderInput={(params) => <TextField {...params} label="Stain" />}
            fullWidth
            disableClearable
            sx={{ mb: 2 }}
          />
          <TextField
            label="Slides"
            type="number"
            value={editSlides}
            onChange={e => setEditSlides(Math.max(1, parseInt(e.target.value) || 1))}
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Slide Labels"
            value={editSlideLabels}
            onChange={e => setEditSlideLabels(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            helperText="Comma-separated labels. Auto-generated when slides changes."
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleEditBlockConfirm} disabled={!editBlockId || !editCassetteLabel || !editBlockStain || !editSlides || !editSlideLabels}>Save</Button>
          </Box>
        </Box>
      </Dialog>
    </Paper>
  );
};

export default HistologySection; 